import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';

import { SpotRed } from '@travel/icons/ui';

import makeMarkerWithLabel from 'utils/markerWithLabel';

import { TRANSPARENT_ICON } from './index';

import styles from './mapPin.module.scss';

type AreaLocation = {
  lat: number;
  lng: number;
};

type SearchedSpotMapPinProps = {
  /** Instance of map */
  map: google.maps.Map | null;
  /** AreaName of searched result */
  areaName: string;
  /** Number for z-index to render the pin with specific order */
  zIndex?: number;
  /** location info for this area */
  areaLocation: AreaLocation;
  /** Callback to be called when Marker is created */
  storeMapPinInListAndMap?: (id: string, mapPin: google.maps.Marker) => void;
  /** Callback to be called when searched spot map pin item is clicked */
  onSearchedSpotMapPinClick?: (itemId: string) => void;
  /** Reset selected map pin by id */
  resetMapPin?: (id: string) => void;
};

function SearchedSpotMapPin(props: SearchedSpotMapPinProps) {
  const { areaName, map, zIndex, areaLocation, storeMapPinInListAndMap, resetMapPin } = props;

  const getLabelContent = useCallback(() => {
    const content = <SpotRed size={36} className={styles.spot} />;

    const labelWrapper = document.createElement('div');
    labelWrapper.setAttribute('data-testid', `mapPin-wrapper-${areaName.replace(/ /g, '')}`);

    ReactDOM.render(content, labelWrapper);
    return labelWrapper;
  }, [areaName]);

  useEffect(() => {
    // reset prev pin when re-render
    resetMapPin?.(areaName);

    const MarkerWithLabel = makeMarkerWithLabel(window.google.maps);
    const position = {
      lat: areaLocation.lat,
      lng: areaLocation.lng,
    };

    const labelContent = getLabelContent();
    const mapPin = new MarkerWithLabel({
      position: position,
      map: map,
      icon: TRANSPARENT_ICON,
      labelContent: labelContent,
      labelClass: styles.label,
      zIndex,
    });

    storeMapPinInListAndMap?.(areaName, mapPin);

    mapPin.addListener('click', () => {
      props.onSearchedSpotMapPinClick?.(areaName);
      map?.setCenter?.(position);
    });
  }, [
    areaLocation.lat,
    areaLocation.lng,
    areaName,
    getLabelContent,
    map,
    props,
    resetMapPin,
    storeMapPinInListAndMap,
    zIndex,
  ]);

  return null;
}

export default SearchedSpotMapPin;
